import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ContentChild,
} from '@angular/core';

export interface TabType {
  id: string;
  label: string;
  index?: number;
  type?: 'scroll' | 'component';
}

@Component({
  selector: 'app-tab-scroller',
  templateUrl: './tab-scroller.component.html',
  styleUrls: ['./tab-scroller.component.scss'],
})
export class TabScrollerComponent implements OnInit, OnDestroy {
  @ContentChild(TemplateRef) templateRef: TemplateRef<any>;

  @Output() tabSelected = new EventEmitter<TabType>();

  intersectingTabs: boolean[] = [];
  ignoreIntersect: boolean;

  _tabs: TabType[] = [];

  private observer: IntersectionObserver;
  private timeoutId: any;

  @Input('tabs')
  public set tabs(value: TabType[]) {
    this._tabs = value.map((tab, index) => ({ ...tab, index }));
    this.intersectingTabs = this._tabs.map(() => false);
    if (value.length > 0) {
      setTimeout(() => this.initScrollObserver(this._tabs));
    }
  }

  public get tabs(): TabType[] {
    return this._tabs;
  }

  _tabIndex: number = 0;

  public get tabIndex(): number {
    return this._tabIndex;
  }

  public set tabIndex(value: number) {
    this._tabIndex = value;
  }

  constructor(private renderer: Renderer2) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  private initScrollObserver(tabs: TabType[]) {
    const scrollableTabs = tabs.filter((tab) => (tab.type ?? 'scroll') === 'scroll');

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const tabIndex = this.tabs.findIndex((tab) => tab.id === entry.target.id);
          if (tabIndex > -1) {
            this.intersectingTabs[tabIndex] = entry.isIntersecting;
          }
        });

        if (!this.ignoreIntersect) {
          const firstVisibleIndex = this.intersectingTabs.findIndex((isVisible) => isVisible);
          if (firstVisibleIndex > -1) {
            this.tabIndex = firstVisibleIndex;
          }
        }
      },
      { rootMargin: '-100px 0px -50% 0px' },
    );

    scrollableTabs.forEach((tab) => {
      const observedDiv = this.renderer.selectRootElement(`#${tab.id}`, true);
      if (observedDiv) {
        this.observer.observe(observedDiv);
      }
    });
  }

  public onTabClick(tab: TabType) {
    this.tabIndex = tab.index;
    this.ignoreIntersect = true;
    this.tabSelected.emit(tab);

    if ((tab.type ?? 'scroll') === 'scroll') {
      const element = document.getElementById(tab.id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.ignoreIntersect = false;
    }, 800);
  }
}
